import { useEffect, useState } from "react";
import { TrendingUp, CheckCircle2, Trophy } from "lucide-react";
import { useAppState } from "@/context/StateContext";
import { api } from "@/lib/api";
import { fmt, pct } from "@/lib/format";

export default function Progress() {
  const { state } = useAppState();
  const [sprints, setSprints] = useState([]);

  useEffect(() => {
    api.listSprints().then(setSprints).catch(() => setSprints([]));
  }, [state]);

  if (!state) return null;
  const { totals, eliminated_debts } = state;
  const completed = sprints.filter((s) => s.status === "completed");
  const progress = pct(totals.total_paid_down, totals.total_original_debt);

  return (
    <div className="fade-up space-y-6" data-testid="progress-view">
      <header>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-[#DDE7DF] px-3 py-1 text-xs font-semibold uppercase tracking-[0.12em] text-[#164C3B]">
          <TrendingUp className="h-3.5 w-3.5" /> Progress
        </span>
        <h1 className="mt-3 font-serif text-3xl text-[#171717] sm:text-4xl">
          Look how far you've come.
        </h1>
      </header>

      {/* Focal metric: total debt attacked */}
      <div
        className="rounded-2xl border border-[#164C3B] bg-[#164C3B] p-6 text-white shadow-[0_8px_30px_-8px_rgba(22,76,59,0.5)]"
        data-testid="total-attacked-card"
      >
        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#C9A86A]">
          Total debt attacked
        </p>
        <p
          className="mt-1.5 font-serif text-5xl tracking-tight text-[#C9A86A]"
          data-testid="stat-total-attacked"
        >
          {fmt(totals.total_paid_down)}
        </p>
        <div className="mt-5 h-2.5 w-full overflow-hidden rounded-full bg-white/15">
          <div
            className="h-full rounded-full bg-[#C9A86A] transition-all duration-700"
            style={{ width: `${progress}%` }}
          />
        </div>
        <div className="mt-2.5 flex items-center justify-between text-sm text-white/70">
          <span>{fmt(totals.total_original_debt)} starting</span>
          <span className="font-semibold text-white">
            {fmt(totals.total_remaining_debt)} remaining
          </span>
        </div>
      </div>

      {/* Two supporting metrics */}
      <div className="grid grid-cols-2 gap-4">
        <div className="rounded-2xl pe-surface p-5" data-testid="stat-debts-eliminated">
          <p className="font-serif text-3xl text-[#164C3B]">{eliminated_debts.length}</p>
          <p className="mt-1 text-sm text-[#70584A]">Debts eliminated</p>
        </div>
        <div className="rounded-2xl pe-surface p-5" data-testid="stat-sprints-completed">
          <p className="font-serif text-3xl text-[#164C3B]">{completed.length}</p>
          <p className="mt-1 text-sm text-[#70584A]">Edits completed</p>
        </div>
      </div>

      {/* Your wins */}
      <div className="rounded-2xl pe-surface p-6" data-testid="your-wins-card">
        <div className="flex items-center gap-2">
          <Trophy className="h-5 w-5 text-[#C9A86A]" />
          <p className="font-serif text-xl text-[#164C3B]">Your wins</p>
        </div>
        {eliminated_debts.length === 0 ? (
          <p className="mt-3 text-sm text-[#525252]">
            No debts cleared yet. Your first paid-off debt will show up here.
          </p>
        ) : (
          <ul className="mt-4 space-y-2.5" data-testid="wins-list">
            {eliminated_debts.map((d) => (
              <li
                key={d.id}
                className="flex items-center justify-between rounded-xl bg-[#F5F2EC] px-4 py-3"
                data-testid={`win-${d.id}`}
              >
                <span className="flex items-center gap-2 font-medium text-[#164C3B]">
                  <CheckCircle2 className="h-4 w-4 text-[#2F6B57]" />
                  {d.name} paid off
                </span>
                <span className="text-sm text-[#70584A]">{fmt(d.original_balance)} cleared</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
